import { Link } from "@tanstack/react-router";
import { Check, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { pricingPlans, type PricingPlan } from "@/lib/finendra-data";
import { useCmsList } from "@/hooks/use-cms";
import { getStarterPricing } from "@/lib/feature-flags";
import { withUTM } from "@/lib/utm";

export function PricingGrid() {
  const plans = useCmsList("plan", pricingPlans);
  const starterConfig = getStarterPricing();

  return (
    <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2 xl:grid-cols-4">
      {plans.map((plan: PricingPlan, i) => {
        const isStarter =
          plan.name.toLowerCase() === "starter" || plan.name.toLowerCase() === "essential";
        const displayPrice = isStarter
          ? starterConfig.starterDisplay
          : plan.price !== null && plan.price !== undefined
            ? `$${plan.price.toLocaleString("en-US")}`
            : "Custom";

        const ctaHref = plan.href || `/contact?plan=${plan.name.toLowerCase()}`;
        const targetHref = withUTM(ctaHref, {
          plan: plan.name.toLowerCase(),
          source: "pricing_card",
          page: "pricing",
        });

        return (
          <Reveal key={plan.name} delay={(i % 4) * 0.07} className="h-full">
            <article
              className={`relative flex h-full flex-col overflow-hidden rounded-3xl border bg-background p-8 transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-1 hover:shadow-xl ${
                plan.popular || plan.featured
                  ? "border-brand-accent bg-background shadow-xl ring-2 ring-brand-accent/20"
                  : "border-brand-navy/10"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex w-fit rounded-full border px-3.5 py-1 text-xs font-bold uppercase tracking-wider ${
                    plan.popular || plan.featured
                      ? "border-transparent bg-brand-accent text-background"
                      : "border-brand-navy/15 text-brand-navy/70 bg-brand-surface"
                  }`}
                >
                  {plan.badge || (plan.popular ? "Most Popular" : plan.name)}
                </span>

                {plan.popular && (
                  <span className="flex items-center gap-1 text-[0.7rem] font-bold text-brand-accent uppercase tracking-wider">
                    <Sparkles className="size-3" /> Recommended
                  </span>
                )}
              </div>

              <h3 className="mt-6 font-display text-2xl font-bold text-brand-navy tracking-tight">
                {plan.name}
              </h3>

              <div className="mt-4">
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  {plan.price !== null && (
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-navy/60">
                      From
                    </span>
                  )}
                  <span className="font-display text-4xl font-extrabold text-brand-navy tracking-tight">
                    {displayPrice}
                  </span>
                  {plan.price !== null && (
                    <span className="text-sm font-semibold text-brand-navy/60">
                      /{plan.interval || "mo"}
                    </span>
                  )}
                </div>

                {plan.bestFor && (
                  <p className="mt-3 text-xs font-semibold leading-relaxed text-brand-navy/70 border-b border-brand-navy/10 pb-4">
                    {plan.bestFor}
                  </p>
                )}

                {plan.note && (
                  <p className="mt-2 text-[0.75rem] font-medium text-brand-accent">{plan.note}</p>
                )}
              </div>

              <div className="mt-6 flex-1">
                <h4 className="text-xs font-bold uppercase tracking-wider text-brand-navy/80 mb-3">
                  What's included:
                </h4>
                <ul className="space-y-3 text-sm leading-relaxed text-brand-navy/75">
                  {plan.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2.5">
                      <Check
                        className="mt-0.5 size-4 shrink-0 text-brand-accent"
                        strokeWidth={2.5}
                      />
                      <span
                        className={
                          inc.startsWith("Everything in") ? "font-bold text-brand-navy" : ""
                        }
                      >
                        {inc}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-navy/10">
                <Link
                  to="/contact"
                  search={{ plan: plan.name.toLowerCase() }}
                  className={`block w-full rounded-xl py-3.5 text-center text-sm font-bold shadow-xs transition-[background-color,color,box-shadow,transform] duration-150 active:scale-[0.98] ${
                    plan.popular || plan.featured
                      ? "bg-brand-navy text-background hover:bg-brand-accent hover:shadow-lg"
                      : "border border-brand-navy/20 bg-background text-brand-navy hover:bg-brand-surface"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            </article>
          </Reveal>
        );
      })}
    </div>
  );
}
